import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faClock, faFire } from '@fortawesome/free-solid-svg-icons';
import { useProductSearch } from '../hooks/useProductSearch';
import { usePartneredBrands } from '../hooks/usePartneredBrands';
import { getRecentSearches, clearRecentSearches } from '../utils/recentSearches';
import '../StylesComponents/header-search-bar.css';

function formatCount(count) {
  if (count == null) return null;
  if (count >= 1000) return `${Math.round(count / 100) / 10}k`;
  return String(count);
}

function HeaderSearchBar({ searchQuery, setSearchQuery }) {
  const wrapperRef = useRef(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const partneredBrands = usePartneredBrands(6);
  const {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    handleSearch,
    handleSmartSearch
  } = useProductSearch(searchQuery);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuggestions && wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions, setShowSuggestions]);

  const handleFocus = () => {
    setRecentSearches(getRecentSearches());
    setShowSuggestions(true);
  };

  const handleRecentClick = (term) => {
    setSearchQuery(term);
    handleSearch(term);
  };

  const handlePartnerClick = (brand) => {
    setSearchQuery(brand);
    handleSmartSearch({ type: 'partner', label: brand });
  };

  const handleClearRecent = (e) => {
    e.stopPropagation();
    clearRecentSearches();
    setRecentSearches([]);
  };

  const showEmptyState = showSuggestions && searchQuery.length < 2;
  const showResults = showSuggestions && searchQuery.length >= 2 && suggestions.length > 0;

  return (
    <div className="header-search-wrapper" ref={wrapperRef}>
      <div className="header-search-bar">
        <FontAwesomeIcon icon={faSearch} className="header-search-icon" />
        <input
          type="text"
          className="header-search-input"
          placeholder="Search deals, brands, styles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="off"
          spellCheck="false"
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          onFocus={handleFocus}
          aria-label="Search deals, brands, and styles"
        />
      </div>

      {showResults && (
        <div className="header-search-suggestions animate-slide-up">
          {suggestions.map((s, idx) => (
            <div
              key={`${s.type}-${s.id || s.label}-${idx}`}
              className={`header-suggestion-item ${s.type === 'smart' ? 'smart-match' : ''} ${s.type === 'product' ? 'product-type' : ''}`}
              onClick={() => handleSmartSearch(s)}
            >
              <div className="header-suggestion-main">
                {s.type === 'product' && s.image && (
                  <img
                    src={s.image}
                    alt=""
                    className="header-suggestion-thumb"
                    loading="lazy"
                  />
                )}
                <div className="header-suggestion-label-group">
                  <span className="header-suggestion-label">{s.label}</span>
                  {s.type === 'smart' && (
                    <span className="header-suggestion-sub-label">SMART MATCH</span>
                  )}
                </div>
              </div>
              {s.type !== 'product' && (
                <div className="header-suggestion-meta">
                  {s.count != null && (
                    <span className="header-suggestion-count">{formatCount(s.count)}</span>
                  )}
                  <span className="header-suggestion-type-tag">{s.type.toUpperCase()}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showEmptyState && (
        <div className="header-search-suggestions header-search-empty-state animate-slide-up">
          {recentSearches.length > 0 && (
            <div className="search-empty-section">
              <div className="search-empty-header">
                <span className="search-empty-title">
                  <FontAwesomeIcon icon={faClock} /> RECENT
                </span>
                <button className="search-clear-btn" onClick={handleClearRecent}>CLEAR</button>
              </div>
              {recentSearches.map((term, idx) => (
                <div key={idx} className="header-suggestion-item" onClick={() => handleRecentClick(term)}>
                  <span className="header-suggestion-label">{term}</span>
                  <span className="header-suggestion-type-tag">SEARCH</span>
                </div>
              ))}
            </div>
          )}
          {partneredBrands.length > 0 && (
            <div className="search-empty-section">
              <div className="search-empty-header">
                <span className="search-empty-title">
                  <FontAwesomeIcon icon={faFire} /> SUGGESTIONS
                </span>
              </div>
              {partneredBrands.map((brand, idx) => (
                <div key={idx} className="header-suggestion-item" onClick={() => handlePartnerClick(brand)}>
                  <span className="header-suggestion-label">{brand}</span>
                  <span className="header-suggestion-type-tag">BRAND</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HeaderSearchBar;
